import { loadFloorFromContent } from '../../domain/dungeon/loadFloor';
import { getNpcById } from '../npcs/npcs';
import { floorFileSchema } from './floorSchema';

const floorOneMerchant = getNpcById('dungeon_merchant');
if (!floorOneMerchant) {
  throw new Error('Missing dungeon_merchant NPC content');
}

/**
 * Floor 1 — ported from mvp1 map.json with gameplay_ideas fixes:
 * - 1 HP: no partial HP damage on escape/trap (lethal or gold/mana cost only)
 * - Combat does not setCleared in content (victory clears later)
 * - Unsafe rest: 25% MP + ambush combat, no free damage HP
 */
const rawFloor01 = floorFileSchema.parse({
  floorNumber: 1,
  floorName: 'Tháp Mê Cung · Tầng 01',
  gridWidth: 5,
  gridHeight: 4,
  objectiveText: 'Tìm ma trận dịch chuyển xuống tầng sâu hơn',
  root: { x: 0, y: 0 },
  safeRoomId: '0,0',
  bossRoomId: '3,2',
  teleporterRoomId: '4,2',
  rooms: [
    {
      id: '0,0',
      gridPos: { x: 0, y: 0 },
      type: 'SAFE',
      status: 'EXPLORED',
      name: 'Cổng Vào Tháp Mê Cung',
      description:
        'Nơi bắt đầu hành trình thám hiểm. Một luồng sáng ma thuật bảo vệ khu vực này khỏi quái vật.',
      safetyTag: 'Khu vực an toàn',
      bgImageKey: 'bg_safe_room',
      thumbnailKey: 'room_thumb',
      isCleared: true,
      isRequiredInteraction: false,
      options: [
        {
          id: 'opt_rest_safe',
          action: 'REST_SAFE',
          actionText: '⛺ Nghỉ ngơi tại Thánh Địa (Hồi 100% MP)',
          buttonColor: '#1e88e5',
          consequence: {
            healMpPercent: 100,
            message: '⛺ Sinh khí thánh địa bao phủ! Bạn đã hồi phục 100% Ma Lực.',
          },
        },
      ],
      connections: [
        { direction: 'EAST', targetRoomId: '1,0' },
        { direction: 'SOUTH', targetRoomId: '0,1' },
      ],
    },
    {
      id: '1,0',
      gridPos: { x: 1, y: 0 },
      type: 'NORMAL_ENEMY',
      status: 'DISCOVERED',
      name: 'Hành Lang Đá U Tối',
      description:
        'Tiếng xương va chạm lách cách vang lên trong bóng tối. Lính xương gác cổng đang chặn đường.',
      safetyTag: 'Nguy hiểm (Bắt buộc tương tác)',
      bgImageKey: 'bg_enemy_room',
      thumbnailKey: 'room_thumb',
      isCleared: false,
      isRequiredInteraction: true,
      options: [
        {
          id: 'opt_combat_1_0',
          action: 'COMBAT',
          actionText: '⚔️ Quyết chiến với Lính Xương Cổ Đại',
          buttonColor: '#e74c3c',
          consequence: {
            triggerCombat: true,
            enemyId: 'bone_guard',
            monsterName: 'Lính Xương Cổ Đại',
            message: '⚔️ Lính Xương Cổ Đại chặn lối. Bạn được quyền ra tay trước.',
          },
        },
        {
          id: 'opt_escape_1_0',
          action: 'ESCAPE',
          actionText: '🏃 Bỏ qua / chạy trốn (50% + DEX)',
          buttonColor: '#5a6470',
          consequence: {
            attemptFlee: true,
            retreatToPrevious: true,
            enemyId: 'bone_guard',
            monsterName: 'Lính Xương Cổ Đại',
            message: '🏃 Thử lảng tránh Lính Xương Cổ Đại…',
          },
        },
      ],
      connections: [
        { direction: 'WEST', targetRoomId: '0,0' },
        { direction: 'EAST', targetRoomId: '2,0' },
        { direction: 'SOUTH', targetRoomId: '1,1' },
      ],
    },
    {
      id: '2,0',
      gridPos: { x: 2, y: 0 },
      type: 'TREASURE',
      status: 'UNEXPLORED',
      name: 'Phòng Báu Vật Cổ',
      description: 'Một chiếc rương gỗ bọc đồng sáng lấp lánh nằm ở góc phòng.',
      safetyTag: 'Báu vật (Bắt buộc tương tác)',
      bgImageKey: 'bg_treasure_room',
      thumbnailKey: 'room_thumb',
      isCleared: false,
      isRequiredInteraction: true,
      options: [
        {
          id: 'opt_open_chest_2_0',
          action: 'OPEN_CHEST',
          actionText: '📦 Mở Rương Báu (+500 Vàng)',
          buttonColor: '#f39c12',
          consequence: {
            gainGold: 500,
            setCleared: true,
            message: '📦 Bạn mở rương và tìm thấy 500 đồng Vàng.',
          },
        },
      ],
      connections: [{ direction: 'WEST', targetRoomId: '1,0' }],
    },
    {
      id: '0,1',
      gridPos: { x: 0, y: 1 },
      type: 'NORMAL_ENEMY',
      status: 'DISCOVERED',
      name: 'Lối Đi Rêu Phong',
      description: 'Tơ nhện giăng kín vách đá. Nhện độc khổng lồ chặn lối.',
      safetyTag: 'Nguy hiểm (Bắt buộc tương tác)',
      bgImageKey: 'bg_enemy_room',
      thumbnailKey: 'room_thumb',
      isCleared: false,
      isRequiredInteraction: true,
      options: [
        {
          id: 'opt_combat_spider',
          action: 'COMBAT',
          actionText: '⚔️ Tiêu diệt Nhện Độc Khổng Lồ',
          buttonColor: '#e74c3c',
          consequence: {
            triggerCombat: true,
            enemyId: 'giant_spider',
            monsterName: 'Nhện Độc Khổng Lồ',
            message: '⚔️ Nhện Độc Khổng Lồ lao tới. Bạn ra tay trước.',
          },
        },
        {
          id: 'opt_escape_spider',
          action: 'ESCAPE',
          actionText: '🏃 Bỏ qua / chạy trốn (50% + DEX)',
          buttonColor: '#5a6470',
          consequence: {
            attemptFlee: true,
            retreatToPrevious: true,
            enemyId: 'giant_spider',
            monsterName: 'Nhện Độc Khổng Lồ',
            message: '🏃 Thử lảng tránh Nhện Độc…',
          },
        },
      ],
      connections: [
        { direction: 'NORTH', targetRoomId: '0,0' },
        { direction: 'EAST', targetRoomId: '1,1' },
      ],
    },
    {
      id: '1,1',
      gridPos: { x: 1, y: 1 },
      type: 'NPC',
      status: 'UNEXPLORED',
      name: `${floorOneMerchant.name} · ${floorOneMerchant.title}`,
      description: floorOneMerchant.description,
      safetyTag: 'NPC · Thương nhân',
      bgImageKey: 'bg_npc_room',
      thumbnailKey: 'room_thumb',
      isCleared: true,
      isRequiredInteraction: false,
      npcId: floorOneMerchant.id,
      options: floorOneMerchant.interactions,
      connections: [
        { direction: 'NORTH', targetRoomId: '1,0' },
        { direction: 'WEST', targetRoomId: '0,1' },
        { direction: 'EAST', targetRoomId: '2,1' },
        { direction: 'SOUTH', targetRoomId: '1,2' },
      ],
    },
    {
      id: '2,1',
      gridPos: { x: 2, y: 1 },
      type: 'TRAP',
      status: 'UNEXPLORED',
      name: 'Bẫy Chông Ma Thuật',
      description: 'Sàn nhà phủ rãnh ma thuật. Một sai sót là cái chết (1 HP).',
      safetyTag: 'Bẫy nguy hiểm (Bắt buộc tương tác)',
      bgImageKey: 'bg_trap_room',
      thumbnailKey: 'room_thumb',
      isCleared: false,
      isRequiredInteraction: true,
      options: [
        {
          id: 'opt_disarm_careful',
          action: 'DISARM_TRAP',
          actionText: '🔧 Gỡ bẫy cẩn thận (mất 20 MP)',
          buttonColor: '#2980b9',
          consequence: {
            spendMana: 20,
            setCleared: true,
            message: '🔧 Bạn gỡ bẫy thành công, tiêu hao 20 Ma Lực tập trung.',
          },
        },
        {
          id: 'opt_rush_trap',
          action: 'RUSH_TRAP',
          actionText: '⚠️ Xông qua bẫy (chết nếu trúng — 1 HP)',
          buttonColor: '#e67e22',
          consequence: {
            damageHp: 1,
            message: '⚠️ Bẫy kích hoạt. Với 1 HP, bạn gục tại chỗ.',
          },
        },
      ],
      connections: [
        { direction: 'WEST', targetRoomId: '1,1' },
        { direction: 'EAST', targetRoomId: '3,1' },
        { direction: 'SOUTH', targetRoomId: '2,2' },
      ],
    },
    {
      id: '3,1',
      gridPos: { x: 3, y: 1 },
      type: 'ELITE_ENEMY',
      status: 'UNEXPLORED',
      name: 'Phòng Vệ Kỵ Sĩ',
      description: 'Kỵ sĩ hắc ám canh lối vào sào huyệt boss.',
      safetyTag: 'Nguy hiểm cao (Bắt buộc tương tác)',
      bgImageKey: 'bg_enemy_room',
      thumbnailKey: 'room_thumb',
      isCleared: false,
      isRequiredInteraction: true,
      options: [
        {
          id: 'opt_combat_elite',
          action: 'COMBAT',
          actionText: '⚔️ Thách đấu Kỵ Sĩ Đen (Elite)',
          buttonColor: '#e74c3c',
          consequence: {
            triggerCombat: true,
            enemyId: 'black_knight',
            monsterName: 'Kỵ Sĩ Đen',
            message: '⚔️ Kỵ Sĩ Đen rút kiếm. Bạn ra tay trước.',
          },
        },
        {
          id: 'opt_escape_elite',
          action: 'ESCAPE',
          actionText: '🏃 Bỏ qua / chạy trốn (50% + DEX)',
          buttonColor: '#5a6470',
          consequence: {
            attemptFlee: true,
            retreatToPrevious: true,
            enemyId: 'black_knight',
            monsterName: 'Kỵ Sĩ Đen',
            message: '🏃 Thử lảng tránh Kỵ Sĩ Đen…',
          },
        },
      ],
      connections: [
        { direction: 'WEST', targetRoomId: '2,1' },
        { direction: 'SOUTH', targetRoomId: '3,2' },
      ],
    },
    {
      id: '1,2',
      gridPos: { x: 1, y: 2 },
      type: 'SECRET',
      status: 'UNEXPLORED',
      name: 'Mật Thất Ẩn',
      description: 'Bức tường đá hé lộ gian phòng ẩn.',
      safetyTag: 'Bí mật (Bắt buộc tương tác)',
      bgImageKey: 'bg_secret_room',
      thumbnailKey: 'room_thumb',
      isCleared: false,
      isRequiredInteraction: true,
      options: [
        {
          id: 'opt_secret_vault',
          action: 'OPEN_CHEST',
          actionText: '👁️ Mở rương mật thất (+1000 Vàng + Sách Phép)',
          buttonColor: '#8e44ad',
          consequence: {
            gainGold: 1000,
            grantItemId: 'tome_radiant_bolt',
            grantItemQty: 1,
            setCleared: true,
            message: '👁️ Mật thất mở ra! +1000 Vàng và Sách Phép Quang Tiễn.',
          },
        },
      ],
      connections: [{ direction: 'NORTH', targetRoomId: '1,1' }],
    },
    {
      id: '2,2',
      gridPos: { x: 2, y: 2 },
      type: 'EMPTY',
      status: 'UNEXPLORED',
      name: 'Sảnh Trung Tâm U Tối',
      description: 'Căn phòng trống u ám. Nghỉ ở đây rất nguy hiểm.',
      safetyTag: 'Không an toàn',
      bgImageKey: 'bg_enemy_room',
      thumbnailKey: 'room_thumb',
      isCleared: true,
      isRequiredInteraction: false,
      options: [
        {
          id: 'opt_rest_unsafe',
          action: 'REST_UNSAFE',
          actionText: '💤 Nghỉ tạm (Hồi 25% MP — chắc chắn phục kích)',
          buttonColor: '#7f8c8d',
          consequence: {
            healMpPercent: 25,
            triggerCombat: true,
            enemyId: 'ambusher',
            monsterName: 'Kẻ Phục Kích',
            message: '⚡ Bạn vừa chợp mắt — kẻ địch phục kích! Hồi 25% MP.',
          },
        },
      ],
      connections: [
        { direction: 'NORTH', targetRoomId: '2,1' },
        { direction: 'EAST', targetRoomId: '3,2' },
      ],
    },
    {
      id: '3,2',
      gridPos: { x: 3, y: 2 },
      type: 'BOSS',
      status: 'UNEXPLORED',
      name: 'Sào Huyệt Trùm Tầng 1',
      description: 'Demon Commander đợi sẵn với trọng kiếm hắc ám.',
      safetyTag: 'Phòng Boss (Bắt buộc tương tác)',
      bgImageKey: 'bg_boss_room',
      thumbnailKey: 'room_thumb',
      isCleared: false,
      isRequiredInteraction: true,
      options: [
        {
          id: 'opt_combat_boss',
          action: 'COMBAT_BOSS',
          actionText: '💀 Khiêu chiến Demon Commander',
          buttonColor: '#c0392b',
          consequence: {
            triggerCombat: true,
            enemyId: 'demon_commander',
            bossName: 'Demon Commander',
            message: '💀 Đại chiến Boss Tầng 1. Bạn ra tay trước.',
          },
        },
      ],
      connections: [
        { direction: 'NORTH', targetRoomId: '3,1' },
        { direction: 'WEST', targetRoomId: '2,2' },
        { direction: 'EAST', targetRoomId: '4,2' },
      ],
    },
    {
      id: '4,2',
      gridPos: { x: 4, y: 2 },
      type: 'TELEPORTER',
      status: 'UNEXPLORED',
      name: 'Ma Trận Dịch Chuyển',
      description: 'Vòng tròn ma thuật dẫn tới tầng tiếp theo.',
      safetyTag: 'Cổng dịch chuyển',
      bgImageKey: 'bg_teleporter_room',
      thumbnailKey: 'room_thumb',
      isCleared: true,
      isRequiredInteraction: false,
      options: [
        {
          id: 'opt_teleport',
          action: 'TELEPORT',
          actionText: '🌀 Bước vào cổng (Tầng 2 — stub)',
          buttonColor: '#9b59b6',
          consequence: {
            nextFloor: 2,
            message: '🌀 Ma trận sáng lên… (chưa có tầng 2 — đánh dấu mục tiêu hoàn thành).',
          },
        },
      ],
      connections: [{ direction: 'WEST', targetRoomId: '3,2' }],
    },
  ],
});

export function createFloor01() {
  return loadFloorFromContent(rawFloor01);
}
